import { 
    analyzeHeadline, 
    analyzeAbout, 
    analyzeExperience, 
    analyzeSkills, 
    calculateKeywordHeatmap, 
    validateCustomUrl, 
    extractKeywordMatch, 
    generateHeadlineVariants, 
    calculateOverallScore 
} from '../linkedinOptimizer.js';

// DOM Element References
const headlineInput = document.getElementById('headlineInput');
const aboutInput = document.getElementById('aboutInput');
const nameInput = document.getElementById('nameInput');
const industrySelect = document.getElementById('industrySelect');
const urlInput = document.getElementById('urlInput');
const skillsInput = document.getElementById('skillsInput');
const jobEntriesContainer = document.getElementById('jobEntriesContainer');
const addJobBtn = document.getElementById('addJobBtn');
const expInput = document.getElementById('expInput');

// Mockup Controls
const openToWorkToggle = document.getElementById('openToWorkToggle');
const degreeSelect = document.getElementById('degreeSelect');

function collectJobsFromCards() {
    const cards = jobEntriesContainer ? jobEntriesContainer.querySelectorAll('.job-entry-card') : [];
    const jobs = [];
    cards.forEach(card => {
        const title = card.querySelector('.job-title-input')?.value || '';
        const company = card.querySelector('.job-company-input')?.value || '';
        const description = card.querySelector('.job-desc-input')?.value || '';
        if (title || company || description) {
            jobs.push({ title, company, description });
        }
    });
    return jobs;
}

function updateOptimizer() {
    if (!headlineInput || !aboutInput) return;

    const headline = headlineInput.value || '';
    const about = aboutInput.value || '';
    const name = (nameInput?.value || '').trim() || 'Alex Morgan';
    const industry = industrySelect?.value || 'tech';
    const url = urlInput?.value || '';
    const skillsText = skillsInput?.value || '';

    const jobsArray = collectJobsFromCards();
    const combinedExpText = jobsArray.map(j => `${j.title} ${j.company}\n${j.description}`).join('\n\n');
    if (expInput) expInput.value = combinedExpText;

    const combinedFullText = `${headline} ${about} ${combinedExpText} ${skillsText}`;

    // Perform analysis
const countrySelect = document.getElementById('countrySelect');
    const country = countrySelect?.value || '';
    const headlineResult = analyzeHeadline(headline);
    const aboutResult = analyzeAbout(about);
    const expResult = analyzeExperience(jobsArray.length > 0 ? jobsArray : combinedExpText);
    const skillsResult = analyzeSkills(skillsText, industry, country);
    const keywordResult = extractKeywordMatch(combinedFullText, industry, country);
    const urlResult = validateCustomUrl(url);
    const overallScore = calculateOverallScore(headlineResult, aboutResult, expResult, skillsResult, keywordResult, urlResult);

    // 1. Overall Score & Rating
    const scoreBadge = document.getElementById('overallScore');
    const scoreBar = document.getElementById('overallScoreBar');
    const scoreRatingText = document.getElementById('scoreRatingText');

    if (scoreBadge) scoreBadge.textContent = overallScore;
    if (scoreBar) scoreBar.style.width = `${overallScore}%`;

    if (scoreRatingText) {
        if (overallScore >= 85) {
            scoreRatingText.textContent = '🌟 Top 5% All-Star Profile! Excellent keyword alignment, metrics & truncation safety.';
            scoreRatingText.style.color = '#059669';
        } else if (overallScore >= 65) {
            scoreRatingText.textContent = '⚡ Strong Profile score! Add more metrics & missing recruiter keywords for All-Star status.';
            scoreRatingText.style.color = '#4f46e5';
        } else if (overallScore >= 40) {
            scoreRatingText.textContent = '⚠️ Moderate score. Headline or summary is truncated or missing key recruiter terms.';
            scoreRatingText.style.color = '#d97706';
        } else {
            scoreRatingText.textContent = '❗ Needs Optimization. Fill out headline, summary, and experience to rank in recruiter searches.';
            scoreRatingText.style.color = '#dc2626';
        }
    }

    // 2. Category Breakdown
    const setCatScore = (scoreId, barId, scoreVal) => {
        const sEl = document.getElementById(scoreId);
        const bEl = document.getElementById(barId);
        const catScore = Math.round((scoreVal / 100) * 25);
        if (sEl) sEl.textContent = `${catScore}/25`;
        if (bEl) {
            bEl.style.width = `${scoreVal}%`;
            bEl.style.backgroundColor = scoreVal >= 80 ? '#059669' : scoreVal >= 50 ? '#4f46e5' : '#d97706';
        }
    };
    setCatScore('catHeadlineScore', 'catHeadlineBar', headlineResult.score);
    setCatScore('catAboutScore', 'catAboutBar', aboutResult.score);
    setCatScore('catExpScore', 'catExpBar', expResult.score);
    setCatScore('catSkillsScore', 'catSkillsBar', skillsResult.score);

    // 3. All-Star Checklist Update
    updateAllStarChecklist(headlineResult, aboutResult, keywordResult, expResult, urlResult);

    // 4. Headline Card
    document.getElementById('headlineCharCount').textContent = headlineResult.length;
    document.getElementById('headlineMaxCount').textContent = headlineResult.max;
    document.getElementById('headlineWordCount').textContent = headlineResult.words;
    const hRem = document.getElementById('headlineRemaining');
    if (hRem) {
        hRem.textContent = headlineResult.isOver ? `${Math.abs(headlineResult.max - headlineResult.length)} over limit` : `${headlineResult.max - headlineResult.length} remaining`;
        hRem.style.color = headlineResult.isOver ? '#dc2626' : '#059669';
    }

    const hBar = document.getElementById('headlineProgressBar');
    if (hBar) {
        const hPct = Math.min((headlineResult.length / headlineResult.max) * 100, 100);
        hBar.style.width = `${hPct}%`;
        hBar.style.backgroundColor = headlineResult.isOver ? '#dc2626' : hPct > 80 ? '#f59e0b' : '#0077b5';
    }

    const hTrunc = document.getElementById('headlineTruncation');
    if (hTrunc) {
        if (headlineResult.length > headlineResult.desktopTruncate) {
            hTrunc.innerHTML = `
                <div class="alert alert-warning" style="margin-top:0.75rem;">
                    <strong>⚠️ LinkedIn Search Cutoff:</strong> Search cards truncate after ~${headlineResult.desktopTruncate} chars on desktop:
                    <div class="search-snippet-box">"${headlineResult.desktopPreview}"</div>
                </div>
            `;
        } else {
            hTrunc.innerHTML = '';
        }
    }
    renderFeedback('headlineTips', headlineResult.feedback);

    // 5. About Card
    document.getElementById('aboutCharCount').textContent = aboutResult.length;
    document.getElementById('aboutWordCount').textContent = aboutResult.words;
    document.getElementById('aboutMaxCount').textContent = aboutResult.max;
    const aRem = document.getElementById('aboutRemaining');
    if (aRem) {
        aRem.textContent = aboutResult.isOver ? `${Math.abs(aboutResult.max - aboutResult.length)} over limit` : `${aboutResult.max - aboutResult.length} remaining`;
        aRem.style.color = aboutResult.isOver ? '#dc2626' : '#059669';
    }

    const aBar = document.getElementById('aboutProgressBar');
    if (aBar) {
        const aPct = Math.min((aboutResult.length / aboutResult.max) * 100, 100);
        aBar.style.width = `${aPct}%`;
        aBar.style.backgroundColor = aboutResult.isOver ? '#dc2626' : aPct > 80 ? '#f59e0b' : '#0077b5';
    }

    const aTrunc = document.getElementById('aboutTruncation');
    if (aTrunc) {
        if (aboutResult.length > aboutResult.searchTruncate) {
            aTrunc.innerHTML = `
                <div class="alert alert-info" style="margin-top:0.75rem;">
                    <strong>👁️ "...see more" Cutoff:</strong> Recruiters see only the first ~${aboutResult.searchTruncate} chars before expanding:
                    <div class="search-snippet-box">"${aboutResult.truncatedPreview}"</div>
                </div>
            `;
        } else {
            aTrunc.innerHTML = '';
        }
    }
    renderFeedback('aboutTips', aboutResult.feedback);

    // 6. Experience Card
    const expWordCount = document.getElementById('expWordCount');
    const expBulletCount = document.getElementById('expBulletCount');
    const expMetricCount = document.getElementById('expMetricCount');
    if (expWordCount) expWordCount.textContent = expResult.words;
    if (expBulletCount) expBulletCount.textContent = expResult.bulletCount;
    if (expMetricCount) expMetricCount.textContent = `${expResult.metricsCount} metric${expResult.metricsCount === 1 ? '' : 's'} detected`;
    renderFeedback('expTips', expResult.feedback);

    // 7. Skills Card
    const skillsCountBadge = document.getElementById('skillsCountBadge');
    if (skillsCountBadge) skillsCountBadge.textContent = `${skillsResult.count} / 50 Skills`;
    renderFeedback('skillsTips', skillsResult.feedback);

    // 8. Keyword Placement Heatmap
    renderKeywordHeatmap(headline, about, combinedExpText, skillsText, industry);

    // 9. Recruiter SEO Keywords Audit
    const matchedContainer = document.getElementById('matchedKeywords');
    const missingContainer = document.getElementById('missingKeywords');
    const keywordMatchPct = document.getElementById('keywordMatchPct');

    if (keywordMatchPct) keywordMatchPct.textContent = `${keywordResult.matchPercentage}% Match`;

    if (matchedContainer) {
        matchedContainer.innerHTML = keywordResult.matched.length > 0
            ? keywordResult.matched.map(kw => `<span class="chip chip-success">✓ ${kw}</span>`).join('')
            : '<span class="text-muted" style="font-size:0.825rem; color:#94a3b8;">No target industry keywords detected yet.</span>';
    }

    if (missingContainer) {
        missingContainer.innerHTML = keywordResult.missing.length > 0
            ? keywordResult.missing.map(kw => `<button type="button" class="chip chip-add" data-kw="${kw}">+ ${kw}</button>`).join('')
            : '<span class="chip chip-success">🎉 All top industry keywords detected!</span>';

        missingContainer.querySelectorAll('.chip-add').forEach(btn => {
            btn.addEventListener('click', () => {
                const kw = btn.getAttribute('data-kw');
                if (skillsInput && !skillsInput.value.toLowerCase().includes(kw.toLowerCase())) {
                    skillsInput.value = skillsInput.value ? `${skillsInput.value}, ${kw}` : kw;
                    showToast(`Added "${kw}" to Skills section!`);
                } else if (aboutInput) {
                    aboutInput.value += ` | ${kw}`;
                    showToast(`Added "${kw}" to About section!`);
                }
                updateOptimizer();
            });
        });
    }

    // 10. Custom URL Audit Card
    const urlScoreBadge = document.getElementById('urlScoreBadge');
    const urlFeedback = document.getElementById('urlFeedback');
    const urlSuggestion = document.getElementById('urlSuggestion');
    const urlSuggestedText = document.getElementById('urlSuggestedText');

    if (urlScoreBadge) urlScoreBadge.textContent = `${urlResult.score}/100`;
    if (urlFeedback) {
        urlFeedback.textContent = urlResult.feedback[0];
        urlFeedback.style.color = urlResult.isValid ? '#059669' : '#dc2626';
    }

    if (urlSuggestion && urlSuggestedText) {
        if (!urlResult.isValid && urlResult.suggestedUrl) {
            urlSuggestion.classList.remove('hidden');
            urlSuggestedText.textContent = urlResult.suggestedUrl;
        } else {
            urlSuggestion.classList.add('hidden');
        }
    }

    // 11. Recruiter Search Simulator (Live Preview)
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || 'AM';
    
    const mockAvatar = document.getElementById('mockAvatar');
    const mockAvatarMob = document.getElementById('mockAvatarMob');
    const mockName = document.getElementById('mockName');
    const mockNameMob = document.getElementById('mockNameMob');
    const mockHeadline = document.getElementById('mockHeadline');
    const mockHeadlineMob = document.getElementById('mockHeadlineMob');
    const mockSnippet = document.getElementById('mockSnippet');
    const mockDegreeBadge = document.getElementById('mockDegreeBadge');
    const openToWorkRing = document.getElementById('openToWorkRing');

    if (mockAvatar) mockAvatar.textContent = initials;
    if (mockAvatarMob) mockAvatarMob.textContent = initials;
    if (mockName) mockName.textContent = name;
    if (mockNameMob) mockNameMob.textContent = name;

    const deg = degreeSelect?.value || '2nd';
    if (mockDegreeBadge) mockDegreeBadge.textContent = deg;

    if (openToWorkRing) {
        if (openToWorkToggle?.checked) {
            openToWorkRing.classList.remove('hidden');
        } else {
            openToWorkRing.classList.add('hidden');
        }
    }

    const previewHeadline = headline || 'Your Headline Will Appear Here in Search Results';
    if (mockHeadline) mockHeadline.textContent = previewHeadline;
    if (mockHeadlineMob) {
        mockHeadlineMob.textContent = previewHeadline.length > 75 
            ? `${previewHeadline.substring(0, 75)}...` 
            : previewHeadline;
    }

    if (mockSnippet) {
        mockSnippet.textContent = about 
            ? (about.length > 170 ? `${about.substring(0, 170)}...` : about) 
            : 'Your summary snippet preview will appear here as you type...';
    }
}

function updateAllStarChecklist(hRes, aRes, kRes, eRes, uRes) {
    const checkHeadline = document.getElementById('checkHeadline');
    const checkAbout = document.getElementById('checkAbout');
    const checkKeywords = document.getElementById('checkKeywords');
    const checkMetrics = document.getElementById('checkMetrics');
    const checkBullets = document.getElementById('checkBullets');
    const checkCustomUrl = document.getElementById('checkCustomUrl');

    let completed = 0;

    const setCheck = (el, isPass, passText, failText) => {
        if (!el) return;
        if (isPass) {
            completed++;
            el.className = 'tip-item tip-pass';
            el.textContent = `✓ ${passText}`;
        } else {
            el.className = 'tip-item';
            el.textContent = `❌ ${failText}`;
        }
    };

    setCheck(checkHeadline, hRes.length >= 40, 'Headline (>40 Chars)', 'Headline (>40 Chars)');
    setCheck(checkAbout, aRes.length >= 300, 'About Summary (>300 Chars)', 'About Summary (>300 Chars)');
    setCheck(checkKeywords, kRes.matchPercentage >= 30, 'Recruiter Keywords Match', 'Recruiter Keywords Match');
    setCheck(checkMetrics, eRes.metricsCount > 0 || aRes.hasMetrics, 'Quantified Results / Metrics', 'Quantified Results / Metrics');
    setCheck(checkBullets, eRes.bulletCount > 0 || aRes.hasBullets, 'Scannable Bullet Symbols', 'Scannable Bullet Symbols');
    setCheck(checkCustomUrl, uRes.isValid, 'Custom Profile URL', 'Custom Profile URL');

    const progressPct = document.getElementById('allStarProgressPct');
    const progressBar = document.getElementById('allStarProgressBar');
    if (progressPct) progressPct.textContent = `${completed}/6 Completed`;
    if (progressBar) {
        const pct = Math.round((completed / 6) * 100);
        progressBar.style.width = `${pct}%`;
        progressBar.style.backgroundColor = completed === 6 ? '#059669' : '#0077b5';
    }
}

function renderKeywordHeatmap(headline, about, experience, skills, industry) {
    const grid = document.getElementById('keywordHeatmapGrid');
    if (!grid) return;

    const heatmap = calculateKeywordHeatmap(headline, about, experience, skills, industry);
    grid.innerHTML = heatmap.map(item => {
        let styleClass = 'background:#f1f5f9; color:#64748b; border:1px solid #cbd5e1;';
        let prefix = '⚪';
        let locText = 'Missing';

        if (item.location === 'headline') {
            styleClass = 'background:#dcfce7; color:#15803d; border:1px solid #bbf7d0; font-weight:700;';
            prefix = '🟢';
            locText = 'Headline';
        } else if (item.location === 'about') {
            styleClass = 'background:#fef9c3; color:#854d0e; border:1px solid #fef08a; font-weight:600;';
            prefix = '🟡';
            locText = 'About';
        } else if (item.location === 'exp_skills') {
            styleClass = 'background:#dbeafe; color:#1e40af; border:1px solid #bfdbfe; font-weight:600;';
            prefix = '🔵';
            locText = 'Exp/Skills';
        }

        return `<span style="font-size:0.775rem; padding:0.25rem 0.65rem; border-radius:100px; display:inline-flex; align-items:center; gap:0.25rem; ${styleClass}">
            <span>${prefix}</span> <strong>${item.keyword}</strong> <span style="opacity:0.75; font-size:0.7rem;">(${locText})</span>
        </span>`;
    }).join('');
}

function renderFeedback(containerId, tipsList) {
    const el = document.getElementById(containerId);
    if (!el) return;
    if (!tipsList || tipsList.length === 0) {
        el.innerHTML = '<div class="tip-item tip-pass">✓ Outstanding section structure!</div>';
        return;
    }
    el.innerHTML = tipsList.map(tip => `<div class="tip-item">${tip}</div>`).join('');
}

function showToast(msg) {
    let toast = document.getElementById('optToast');
    if (!toast) {
        toast = document.createElement('div');
        toast.id = 'optToast';
        toast.className = 'opt-toast';
        document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('show');
    setTimeout(() => toast.classList.remove('show'), 2800);
}

// ─── PDF Extraction & Parsing Engine ───────────────────────────────────────
async function extractTextFromPdf(file) {
    if (typeof window.pdfjsLib === 'undefined') {
        throw new Error('PDF Engine loading... Please try again in 2 seconds.');
    }
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({
        data: arrayBuffer,
        standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/'
    }).promise;

    let fullText = '';
    try {
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const tokenized = await page.getTextContent();
            const items = (tokenized.items || []).filter(item => item && typeof item.str === 'string');

            items.sort((a, b) => {
                const yA = a.transform ? a.transform[5] : 0;
                const yB = b.transform ? b.transform[5] : 0;
                const xA = a.transform ? a.transform[4] : 0;
                const xB = b.transform ? b.transform[4] : 0;
                if (Math.abs(yA - yB) > 6) return yB - yA;
                return xA - xB;
            });

            let pageText = '';
            let lastY = null;
            for (const item of items) {
                const currentY = item.transform ? item.transform[5] : 0;
                if (lastY !== null && Math.abs(currentY - lastY) > 8) pageText += '\n';
                else if (pageText.length > 0 && !pageText.endsWith('\n') && !pageText.endsWith(' ')) pageText += ' ';
                pageText += item.str;
                lastY = currentY;
            }
            fullText += pageText + '\n\n';
        }
    } finally {
        if (pdf && typeof pdf.destroy === 'function') await pdf.destroy();
    }

    const trimmed = fullText.trim();
    if (trimmed.length < 50) {
        throw new Error('SCANNED_PDF_NO_TEXT: We detected minimal readable text in this document.');
    }

    return trimmed;
}

export function parseLinkedInPdfText(text) {
    if (!text) return { name: '', headline: '', about: '', url: '', skills: [], jobs: [] };

    let url = '';
    const linkedinMatch = text.match(/linkedin\.com\/in\/([a-zA-Z0-9_-]+)/i);
    if (linkedinMatch) {
        url = `linkedin.com/in/${linkedinMatch[1]}`;
    }

    const skills = [];
    const skillsMatch = text.match(/Top Skills\s*\n([\s\S]*?)(?=\nCertifications|\nSummary|\nLanguages|\nHonors|\nExperience|\n\n[A-Z][a-z]+ [A-Z][a-z]+)/i);
    if (skillsMatch) {
        const rawSkillsLines = skillsMatch[1].split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
        rawSkillsLines.forEach(line => {
            if (!line.includes('www.linkedin.com') && !line.includes('Page ') && line.length < 50) {
                skills.push(line);
            }
        });
    }

    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);

    const cleanLines = lines.filter(l => 
        !l.toLowerCase().includes('www.linkedin.com') &&
        !l.toLowerCase().startsWith('page ') &&
        !l.toLowerCase().includes('(linkedin)') &&
        !l.toLowerCase().includes('(portfolio)') &&
        !/^\d+ \(\w+\)$/.test(l) &&
        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(l)
    );

    const summaryIdx = cleanLines.findIndex(l => /^summary$/i.test(l) || /^about$/i.test(l));
    const experienceIdx = cleanLines.findIndex(l => /^experience$/i.test(l));

    let name = '';
    let headline = '';

    const headerCandidates = summaryIdx > -1 ? cleanLines.slice(0, summaryIdx) : cleanLines.slice(0, 20);
    
    let nameIdx = -1;
    for (let i = 0; i < headerCandidates.length; i++) {
        const line = headerCandidates[i];
        if (/^contact$/i.test(line) || /^top skills$/i.test(line) || /^certifications$/i.test(line)) {
            continue;
        }
        if (/^[A-Z][a-z]+(?:\s+[A-Z][a-zA-Z.-]+){1,3}$/.test(line)) {
            if (!/server|management|foundation|framework|identity|security|controller/i.test(line)) {
                nameIdx = i;
                name = line;
                break;
            }
        }
    }

    if (nameIdx !== -1) {
        const headlineLines = [];
        for (let j = nameIdx + 1; j < headerCandidates.length; j++) {
            const hLine = headerCandidates[j];
            if (/^summary$/i.test(hLine) || /^experience$/i.test(hLine)) break;
            if (/^contact$/i.test(hLine) || /^top skills$/i.test(hLine) || /^certifications$/i.test(hLine)) break;
            if (/,\s*(United States|United Arab Emirates|Australia|Canada|United Kingdom|Sri Lanka|India|Germany|Singapore|Dubai)/i.test(hLine)) {
                break;
            }
            headlineLines.push(hLine);
        }
        headline = headlineLines.join(' ');
    }

    let about = '';
    if (summaryIdx !== -1) {
        const endSummaryIdx = experienceIdx > summaryIdx ? experienceIdx : cleanLines.length;
        const aboutLines = cleanLines.slice(summaryIdx + 1, endSummaryIdx).filter(l => !/^experience$/i.test(l));
        about = aboutLines.join('\n');
    }

    // Extract Experience Jobs
    const jobs = [];
    if (experienceIdx !== -1) {
        const educationIdx = cleanLines.findIndex(l => /^education$/i.test(l) || /^certifications$/i.test(l));
        const expLines = cleanLines.slice(experienceIdx + 1, educationIdx > experienceIdx ? educationIdx : cleanLines.length);

        let currentJob = null;
        for (let i = 0; i < expLines.length; i++) {
            const line = expLines[i];
            const dateMatch = line.match(/(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\s*-\s*(Present|(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i);

            if (dateMatch) {
                let company = '';
                let title = '';
                if (i >= 2) {
                    company = expLines[i - 2];
                    title = expLines[i - 1];
                } else if (i === 1) {
                    title = expLines[i - 1];
                }

                if (currentJob) {
                    currentJob.descriptionLines = currentJob.descriptionLines.filter(l => l !== company && l !== title);
                    jobs.push(currentJob);
                }

                currentJob = {
                    company,
                    title,
                    descriptionLines: []
                };

                if (i + 1 < expLines.length && /^[A-Za-z\s,-]+$/.test(expLines[i + 1]) && expLines[i + 1].length < 35 && !expLines[i + 1].startsWith('•')) {
                    i++;
                }
            } else if (currentJob) {
                currentJob.descriptionLines.push(line);
            }
        }
        if (currentJob) {
            jobs.push(currentJob);
        }
    }

    const formattedJobs = jobs.map(j => ({
        company: j.company,
        title: j.title,
        description: j.descriptionLines.join('\n').trim()
    }));

    return { name, headline, about, url, skills, jobs: formattedJobs };
}

function populateJobCards(jobs) {
    if (!jobEntriesContainer) return;
    jobEntriesContainer.innerHTML = '';
    
    if (!jobs || jobs.length === 0) {
        addJobCard('', '', '');
        addJobCard('', '', '');
        return;
    }

    jobs.forEach(j => addJobCard(j.title, j.company, j.description));
}

function addJobCard(title = '', company = '', description = '') {
    if (!jobEntriesContainer) return;

    const card = document.createElement('div');
    card.className = 'job-entry-card';
    card.style.cssText = 'background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:0.85rem; margin-bottom:0.75rem; position:relative;';

    card.innerHTML = `
        <button type="button" class="btn-remove-job" style="position:absolute; top:8px; right:8px; background:none; border:none; color:#ef4444; font-size:1.1rem; cursor:pointer; font-weight:bold;" title="Remove Position">×</button>
        <div style="display:grid; grid-template-columns: 1fr 1fr; gap:0.5rem; margin-bottom:0.5rem; padding-right:20px;">
            <input type="text" class="job-title-input" placeholder="Job Title (e.g. Senior Systems Engineer)" value="${escapeHtml(title)}">
            <input type="text" class="job-company-input" placeholder="Company (e.g. Cloud Tech Corp)" value="${escapeHtml(company)}">
        </div>
        <textarea class="job-desc-input" style="min-height:90px; width:100%; font-size:0.85rem;" placeholder="Bullet point accomplishments with numbers... (e.g. • Spearheaded microservices migration handling 10k req/min)">${escapeHtml(description)}</textarea>
    `;

    jobEntriesContainer.appendChild(card);

    card.querySelectorAll('input, textarea').forEach(input => {
        input.addEventListener('input', updateOptimizer);
    });

    const removeBtn = card.querySelector('.btn-remove-job');
    if (removeBtn) {
        removeBtn.addEventListener('click', () => {
            card.remove();
            updateOptimizer();
        });
    }
}

function escapeHtml(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function initPdfUploader() {
    const pdfDropzone = document.getElementById('pdfDropzone');
    const pdfFileInput = document.getElementById('pdfFileInput');
    const dropzoneDefault = document.getElementById('dropzoneDefault');
    const dropzoneLoading = document.getElementById('dropzoneLoading');
    const pdfSuccessAlert = document.getElementById('pdfSuccessAlert');
    const pdfFileNameText = document.getElementById('pdfFileNameText');

    if (!pdfDropzone || !pdfFileInput) return;

    pdfDropzone.addEventListener('click', () => pdfFileInput.click());
    pdfDropzone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            pdfFileInput.click();
        }
    });

    pdfDropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        pdfDropzone.classList.add('drag-over');
    });

    pdfDropzone.addEventListener('dragleave', () => {
        pdfDropzone.classList.remove('drag-over');
    });

    pdfDropzone.addEventListener('drop', async (e) => {
        e.preventDefault();
        pdfDropzone.classList.remove('drag-over');
        const file = e.dataTransfer.files[0];
        if (file) await handlePdfFile(file);
    });

    pdfFileInput.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (file) await handlePdfFile(file);
    });

    async function handlePdfFile(file) {
        if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
            showToast('⚠️ Please select a valid PDF file.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            showToast('⚠️ File size exceeds 5MB limit.');
            return;
        }

        if (dropzoneDefault) dropzoneDefault.classList.add('hidden');
        if (dropzoneLoading) dropzoneLoading.classList.remove('hidden');
        if (pdfSuccessAlert) pdfSuccessAlert.classList.add('hidden');

        try {
            const fullText = await extractTextFromPdf(file);
            if (!fullText || fullText.trim().length < 20) {
                throw new Error('Extracted text is empty. The PDF may be scanned or locked.');
            }

            const parsed = parseLinkedInPdfText(fullText);

            if (parsed.name && nameInput) nameInput.value = parsed.name;
            if (parsed.headline && headlineInput) headlineInput.value = parsed.headline;
            if (parsed.about && aboutInput) aboutInput.value = parsed.about;
            if (parsed.url && urlInput) urlInput.value = parsed.url;
            if (parsed.skills.length > 0 && skillsInput) skillsInput.value = parsed.skills.join(', ');

            if (parsed.jobs && parsed.jobs.length > 0) {
                populateJobCards(parsed.jobs);
            }

            updateOptimizer();

            if (pdfSuccessAlert) {
                pdfSuccessAlert.classList.remove('hidden');
                if (pdfFileNameText) pdfFileNameText.textContent = file.name;
            }
            showToast(`✓ Auto-filled profile from "${file.name}"!`);
        } catch (err) {
            console.error('PDF Processing Error:', err);
            showToast(`⚠️ Could not parse PDF: ${err.message}`);
        } finally {
            if (dropzoneDefault) dropzoneDefault.classList.remove('hidden');
            if (dropzoneLoading) dropzoneLoading.classList.add('hidden');
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    // Input Event Listeners for Live Update
    [headlineInput, aboutInput, nameInput, urlInput, skillsInput].forEach(el => {
        if (el) el.addEventListener('input', updateOptimizer);
    });

    if (industrySelect) industrySelect.addEventListener('change', updateOptimizer);
    if (openToWorkToggle) openToWorkToggle.addEventListener('change', updateOptimizer);
    if (degreeSelect) degreeSelect.addEventListener('change', updateOptimizer);

    // Job Entry Listeners
    if (addJobBtn) {
        addJobBtn.addEventListener('click', () => {
            addJobCard('', '', '');
            updateOptimizer();
        });
    }

    // Attach initial job input listeners if cards exist in HTML
    if (jobEntriesContainer) {
        jobEntriesContainer.querySelectorAll('input, textarea').forEach(input => {
            input.addEventListener('input', updateOptimizer);
        });
    }

    // Symbol Palette Copy
    document.querySelectorAll('.symbol-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const sym = btn.getAttribute('data-symbol');
            if (sym) {
                navigator.clipboard.writeText(sym).then(() => {
                    showToast(`Copied "${sym}" to clipboard!`);
                });
            }
        });
    });

    // Copy Field Buttons
    document.querySelectorAll('.btn-copy-field').forEach(btn => {
        btn.addEventListener('click', () => {
            const targetId = btn.getAttribute('data-target');
            const target = document.getElementById(targetId);
            if (target && target.value) {
                navigator.clipboard.writeText(target.value).then(() => {
                    showToast('Copied section text to clipboard!');
                });
            }
        });
    });

    // Sample Data Loader
    const sampleBtn = document.getElementById('loadSampleBtn');
    if (sampleBtn) {
        sampleBtn.addEventListener('click', () => {
            if (headlineInput) headlineInput.value = 'Senior Cloud Solution Architect & Systems Engineer | Multi-Cloud (AWS, Azure) | 10+ Yrs Exp Scaling 99.99% Uptime Networks';
            if (aboutInput) aboutInput.value = 'Dedicated Systems Engineer and Cloud Solution Architect with over a decade of experience optimizing enterprise IT infrastructure and leading seamless cloud migrations.\n\nKey Achievements:\n• Spearheaded cloud migration cutting operational costs by 30% and transition times by 25%.\n• Designed resilient data infrastructure handling 10,000+ user requests per minute.\n• Reduced system vulnerabilities by 40% through advanced security protocols.\n\nOpen to Principal/Lead Architect roles | Email: saranga@example.com';
            if (nameInput) nameInput.value = 'Saranga Thenuwara';
            if (industrySelect) industrySelect.value = 'tech';
            if (urlInput) urlInput.value = 'linkedin.com/in/sthenuwara';
            if (skillsInput) skillsInput.value = 'Cloud Architecture, AWS, Active Directory, Network Management, System Security, Python, Microservices, CI/CD';

            populateJobCards([
                {
                    title: 'Senior SME Engineer',
                    company: '5G Networks Ltd',
                    description: '• Managed robust Network and Data Infrastructure, optimizing Fiber and Ethernet cable management solutions.\n• Delivered state-of-the-art security solutions utilizing cutting-edge technology.'
                },
                {
                    title: 'System Engineer - L2',
                    company: 'Tech One Global Ltd',
                    description: '• Provided tier 2 support for on-prem and private cloud SaaS deployments.\n• Collaborated with cross-functional teams to translate application requirements into optimized system designs.'
                }
            ]);

            updateOptimizer();
            showToast('Loaded All-Star sample profile!');
        });
    }

    // Headline Variant Generator
    const generateHeadlineBtn = document.getElementById('generateHeadlineBtn');
    const headlineVariantsBox = document.getElementById('headlineVariantsBox');
    if (generateHeadlineBtn && headlineVariantsBox) {
        generateHeadlineBtn.addEventListener('click', () => {
            const role = nameInput?.value ? 'Systems Engineer & Cloud Architect' : 'Software Engineer';
            const skills = skillsInput?.value || 'Cloud, AWS, System Design';
            const metric = 'Cutting Migration Costs by 30%';

            const variants = generateHeadlineVariants(role, skills, metric);
            headlineVariantsBox.classList.remove('hidden');
            headlineVariantsBox.innerHTML = variants.map(v => `
                <div style="background:#f0f9ff; border:1px solid #bae6fd; padding:0.6rem; border-radius:6px; font-size:0.825rem; display:flex; justify-content:space-between; align-items:center; gap:0.5rem;">
                    <span style="color:#0369a1; font-weight:600;">"${escapeHtml(v)}"</span>
                    <button type="button" class="btn-use-variant" data-variant="${escapeHtml(v)}" style="background:#0077b5; color:white; border:none; padding:3px 8px; border-radius:4px; font-weight:700; cursor:pointer; flex-shrink:0;">Use</button>
                </div>
            `).join('');

            headlineVariantsBox.querySelectorAll('.btn-use-variant').forEach(b => {
                b.addEventListener('click', () => {
                    const varText = b.getAttribute('data-variant');
                    if (headlineInput && varText) {
                        headlineInput.value = varText;
                        updateOptimizer();
                        showToast('Applied headline variant!');
                    }
                });
            });
        });
    }

    // Action Buttons (Print, Export TXT, Copy Profile, Reset)
    const exportReportBtn = document.getElementById('exportReportBtn');
    if (exportReportBtn) exportReportBtn.addEventListener('click', () => window.print());

    const exportTxtBtn = document.getElementById('exportTxtBtn');
    if (exportTxtBtn) {
        exportTxtBtn.addEventListener('click', () => {
            const name = nameInput?.value || 'Profile';
            const headline = headlineInput?.value || '';
            const about = aboutInput?.value || '';
            const skills = skillsInput?.value || '';
            const exp = expInput?.value || '';

            const content = `LINKEDIN PROFILE OPTIMIZATION REPORT - RESUMEMETRIC
Name: ${name}
Headline: ${headline}

ABOUT / SUMMARY:
${about}

SKILLS:
${skills}

WORK EXPERIENCE:
${exp}
`;
            const blob = new Blob([content], { type: 'text/plain' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `${name.replace(/\s+/g, '_')}_LinkedIn_Profile.txt`;
            a.click();
            showToast('Downloaded profile report (.txt)!');
        });
    }

    const copyCleanTextBtn = document.getElementById('copyCleanTextBtn');
    if (copyCleanTextBtn) {
        copyCleanTextBtn.addEventListener('click', () => {
            const headline = headlineInput?.value || '';
            const about = aboutInput?.value || '';
            const exp = expInput?.value || '';
            const full = `HEADLINE:\n${headline}\n\nABOUT:\n${about}\n\nEXPERIENCE:\n${exp}`;
            navigator.clipboard.writeText(full).then(() => showToast('Copied full profile text!'));
        });
    }

    const resetFormBtn = document.getElementById('resetFormBtn');
    if (resetFormBtn) {
        resetFormBtn.addEventListener('click', () => {
            if (confirm('Are you sure you want to reset all profile data?')) {
                if (headlineInput) headlineInput.value = '';
                if (aboutInput) aboutInput.value = '';
                if (nameInput) nameInput.value = '';
                if (urlInput) urlInput.value = '';
                if (skillsInput) skillsInput.value = '';
                populateJobCards([]);
                updateOptimizer();
                showToast('Form reset!');
            }
        });
    }

    const copyUrlBtn = document.getElementById('copyUrlBtn');
    if (copyUrlBtn) {
        copyUrlBtn.addEventListener('click', () => {
            const sug = document.getElementById('urlSuggestedText')?.textContent;
            if (sug) {
                navigator.clipboard.writeText(sug).then(() => showToast('Copied suggested custom URL!'));
            }
        });
    }

    initPdfUploader();
    updateOptimizer();
});

